/**
 * HostPoint POS - iMin Printer SDK Integration
 * 
 * This module provides integration with iMin POS device built-in printers
 * using the iMin JavaScript SDK (IminPrintInstance).
 * 
 * Documentation: https://oss-sg.imin.sg/docs/en/JSPrinterSDK.html
 * 
 * Connection Types:
 * - USB: For D1w, D4, D1, D1 Pro, M2 Max, Swift 1, Falcon 1
 * - SPI: For M2-202, M2-203, M2 Pro
 * - Bluetooth: BluetoothPrinter device name
 * 
 * SDK V2.0 Models: D4 Pro, Swift 2, Swift 2 Pro, Swift 1 Pro, Swan 2, Falcon 2
 * SDK V1.0 Models: D1w, D4, D1, D1 Pro, M2-202, M2-203, M2 Pro, M2 Max, Swift 1, Falcon 1
 */

class IminPrinterSDK {
  constructor() {
    this.sdk = null;
    this.isInitialized = false;
    this.connectionType = null;
    this.printerStatus = -1;
    this.deviceModel = null;
    this.paperWidth = 80; // Default 80mm
  }

  /**
   * Check if running on an iMin device with SDK available
   */
  isAvailable() {
    // Check for our custom Android wrapper first
    if (typeof window !== 'undefined' && window.HostPointPrinter) {
      return true;
    }
    
    // Check for standard iMin SDK
    if (typeof window !== 'undefined') {
      // V2.0 SDK
      if (window.IminPrintInstance !== undefined) {
        return true;
      }
      // Alternative SDK names
      if (window.IminPrinterInstance !== undefined || 
          window.iminPrinter !== undefined) {
        return true;
      }
      // Android JS interface
      if (window.android && (
        typeof window.android.printText === 'function' ||
        typeof window.android.initPrinter === 'function'
      )) {
        return true;
      }
    }
    return false;
  }

  /**
   * Get the SDK instance
   */
  getSDK() {
    if (!this.sdk && this.isAvailable()) {
      this.sdk = window.IminPrintInstance;
    }
    return this.sdk;
  }

  /**
   * Detect iMin device model from user agent and device characteristics
   */
  detectDeviceModel() {
    const ua = navigator.userAgent.toLowerCase();
    
    const models = [
      { pattern: /d4\s*pro/i, name: 'D4 Pro', sdkVersion: 'V2.0', connection: 'USB', paperWidth: 80 },
      { pattern: /d4/i, name: 'D4', sdkVersion: 'V1.0', connection: 'USB', paperWidth: 80 },
      { pattern: /d1\s*pro/i, name: 'D1 Pro', sdkVersion: 'V1.0', connection: 'USB', paperWidth: 80 },
      { pattern: /d1w/i, name: 'D1w', sdkVersion: 'V1.0', connection: 'USB', paperWidth: 80 },
      { pattern: /d1/i, name: 'D1', sdkVersion: 'V1.0', connection: 'USB', paperWidth: 80 },
      { pattern: /swift\s*2\s*pro/i, name: 'Swift 2 Pro', sdkVersion: 'V2.0', connection: 'USB', paperWidth: 58 },
      { pattern: /swift\s*2/i, name: 'Swift 2', sdkVersion: 'V2.0', connection: 'USB', paperWidth: 58 },
      { pattern: /swift\s*1\s*pro/i, name: 'Swift 1 Pro', sdkVersion: 'V2.0', connection: 'USB', paperWidth: 58 },
      { pattern: /swift\s*1/i, name: 'Swift 1', sdkVersion: 'V1.0', connection: 'USB', paperWidth: 58 },
      { pattern: /swift/i, name: 'Swift', sdkVersion: 'V1.0', connection: 'USB', paperWidth: 58 },
      { pattern: /falcon\s*2/i, name: 'Falcon 2', sdkVersion: 'V2.0', connection: 'USB', paperWidth: 80 },
      { pattern: /falcon\s*1/i, name: 'Falcon 1', sdkVersion: 'V1.0', connection: 'USB', paperWidth: 80 },
      { pattern: /falcon/i, name: 'Falcon', sdkVersion: 'V1.0', connection: 'USB', paperWidth: 80 },
      { pattern: /swan\s*2/i, name: 'Swan 2', sdkVersion: 'V2.0', connection: 'USB', paperWidth: 80 },
      { pattern: /swan/i, name: 'Swan', sdkVersion: 'V2.0', connection: 'USB', paperWidth: 80 },
      { pattern: /m2\s*max/i, name: 'M2 Max', sdkVersion: 'V1.0', connection: 'USB', paperWidth: 58 },
      { pattern: /m2\s*pro/i, name: 'M2 Pro', sdkVersion: 'V1.0', connection: 'SPI', paperWidth: 58 },
      { pattern: /m2-203/i, name: 'M2-203', sdkVersion: 'V1.0', connection: 'SPI', paperWidth: 58 },
      { pattern: /m2-202/i, name: 'M2-202', sdkVersion: 'V1.0', connection: 'SPI', paperWidth: 58 },
      { pattern: /m2/i, name: 'M2', sdkVersion: 'V1.0', connection: 'SPI', paperWidth: 58 },
    ];

    // First check user agent
    for (const model of models) {
      if (model.pattern.test(ua)) {
        this.deviceModel = model;
        return model;
      }
    }

    // If SDK is available, try to detect from SDK if possible
    if (this.isAvailable()) {
      // Try to get device model from Android bridge if available
      try {
        if (window.android && typeof window.android.getDeviceModel === 'function') {
          const androidModel = window.android.getDeviceModel();
          for (const model of models) {
            if (model.pattern.test(androidModel)) {
              this.deviceModel = model;
              return model;
            }
          }
        }
      } catch (e) {
      }

      // Check screen dimensions for common iMin devices
      const screenWidth = window.screen.width;
      const screenHeight = window.screen.height;
      
      // D4 typically has 1080x1920 or 1920x1080
      if ((screenWidth === 1080 && screenHeight === 1920) || (screenWidth === 1920 && screenHeight === 1080)) {
        this.deviceModel = { name: 'D4 (Detected)', sdkVersion: 'V1.0', connection: 'USB', paperWidth: 80 };
        return this.deviceModel;
      }
      
      // Default to generic iMin with USB connection (most common)
      this.deviceModel = { name: 'iMin Device', sdkVersion: 'V1.0', connection: 'USB', paperWidth: 80 };
      return this.deviceModel;
    }

    return null;
  }

  /**
   * Get the connection type enum for iMin SDK
   */
  getConnectionType(type = null) {
    const sdk = this.getSDK();
    if (!sdk || !sdk.PrintConnectType) return null;

    type = type || (this.deviceModel?.connection || 'USB');

    switch (type.toUpperCase()) {
      case 'USB':
        return sdk.PrintConnectType.USB;
      case 'SPI':
        return sdk.PrintConnectType.SPI;
      case 'BLUETOOTH':
        return sdk.PrintConnectType.Bluetooth;
      default:
        return sdk.PrintConnectType.USB;
    }
  }

  /**
   * Initialize the printer
   * @param {string} connectionType - 'USB', 'SPI', or 'Bluetooth'
   */
  async initPrinter(connectionType = null) {
    const sdk = this.getSDK();
    if (!sdk) {
      throw new Error('iMin SDK not available');
    }

    try {
      // Detect device if not already done
      if (!this.deviceModel) {
        this.detectDeviceModel();
      }

      const connType = this.getConnectionType(connectionType);
      if (!connType) {
        throw new Error('Invalid connection type');
      }

      sdk.initPrinter(connType);
      this.connectionType = connectionType || this.deviceModel?.connection || 'USB';
      this.isInitialized = true;

      return { success: true, connectionType: this.connectionType };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get printer status
   * @returns {Promise<number>} Status code:
   *   -1: Not connected or powered on
   *    1: Normal
   *    3: Print head open
   *    7: No paper
   *    8: Paper running out
   *   99: Other errors
   */
  async getPrinterStatus() {
    const sdk = this.getSDK();
    if (!sdk) return -1;

    return new Promise((resolve) => {
      const connType = this.getConnectionType();
      if (!connType) {
        resolve(-1);
        return;
      }

      sdk.getPrinterStatus(connType, (status) => {
        this.printerStatus = status.value;
        resolve(status.value);
      });
    });
  }

  /**
   * Get human-readable printer status
   */
  async getStatusText() {
    const status = await this.getPrinterStatus();
    const statusMap = {
      '-1': 'Not connected',
      '1': 'Ready',
      '3': 'Print head open',
      '7': 'No paper',
      '8': 'Paper low',
      '99': 'Error'
    };
    return statusMap[String(status)] || `Unknown (${status})`;
  }

  /**
   * Set paper format
   * @param {number} width - 58 or 80 (mm)
   */
  setPageFormat(width = 80) {
    const sdk = this.getSDK();
    if (!sdk) return;

    this.paperWidth = width;
    sdk.setPageFormat(width === 58 ? 1 : 0);
  }

  /**
   * Set text alignment
   * @param {string|number} alignment - 'left'/0, 'center'/1, 'right'/2
   */
  setAlignment(alignment) {
    const sdk = this.getSDK();
    if (!sdk) return;

    let alignValue = 0;
    if (typeof alignment === 'string') {
      alignValue = { 'left': 0, 'center': 1, 'right': 2 }[alignment.toLowerCase()] || 0;
    } else {
      alignValue = alignment;
    }
    sdk.setAlignment(alignValue);
  }

  /**
   * Set text size
   * @param {number} size - Font size (default 28)
   */
  setTextSize(size = 28) {
    const sdk = this.getSDK();
    if (sdk) sdk.setTextSize(size);
  }

  /**
   * Set text style
   * @param {string|number} style - 'normal'/0, 'bold'/1, 'italic'/2, 'bold_italic'/3
   */
  setTextStyle(style) {
    const sdk = this.getSDK();
    if (!sdk) return;

    let styleValue = 0;
    if (typeof style === 'string') {
      styleValue = { 'normal': 0, 'bold': 1, 'italic': 2, 'bold_italic': 3 }[style.toLowerCase()] || 0;
    } else {
      styleValue = style;
    }
    sdk.setTextStyle(styleValue);
  }

  /**
   * Print text
   * @param {string} text - Text to print
   * @param {boolean} autoWrap - Auto wrap text (default true)
   */
  printText(text, autoWrap = true) {
    const sdk = this.getSDK();
    if (!sdk) return;

    sdk.printText(text, autoWrap ? 1 : 0);
  }

  /**
   * Print text with styling
   */
  printStyledText(text, options = {}) {
    const { alignment = 'left', size = 28, bold = false } = options;
    
    this.setAlignment(alignment);
    this.setTextSize(size);
    this.setTextStyle(bold ? 'bold' : 'normal');
    this.printText(text);
  }

  /**
   * Print line and feed paper
   */
  printAndLineFeed() {
    const sdk = this.getSDK();
    if (sdk) sdk.printAndLineFeed();
  }

  /**
   * Feed paper by specified height
   * @param {number} height - Feed height (0-255)
   */
  feedPaper(height = 100) {
    const sdk = this.getSDK();
    if (sdk) sdk.printAndFeedPaper(Math.min(255, Math.max(0, height)));
  }

  /**
   * Print a table row
   * @param {string[]} columns - Array of column text
   * @param {number[]} widths - Array of column width weights
   * @param {number[]} alignments - Array of alignments (0=left, 1=center, 2=right)
   * @param {number[]} sizes - Array of font sizes
   */
  printColumnsText(columns, widths, alignments, sizes) {
    const sdk = this.getSDK();
    if (!sdk) return;

    const totalWidth = this.paperWidth === 58 ? 384 : 576;
    sdk.printColumnsText(columns, widths, alignments, sizes, totalWidth);
  }

  /**
   * Print QR code
   * @param {string} content - QR code content
   * @param {object} options - { alignment: 0/1/2, size: 1-16 }
   */
  printQrCode(content, options = {}) {
    const sdk = this.getSDK();
    if (!sdk) return;

    const { alignment = 1, size = 6 } = options;
    sdk.setQrCodeSize(size);
    sdk.printQrCode(content, alignment);
  }

  /**
   * Print barcode
   * @param {string} content - Barcode content
   * @param {number} type - Barcode type (73 = CODE128)
   * @param {object} options - { alignment: 0/1/2, height: 1-255, width: 2-6 }
   */
  printBarCode(content, type = 73, options = {}) {
    const sdk = this.getSDK();
    if (!sdk) return;

    const { alignment = 1, height = 100, width = 3, showText = true } = options;
    
    sdk.setBarCodeWidth(width);
    sdk.setBarCodeHeight(height);
    sdk.setBarCodeContentPrintPos(showText ? 2 : 0); // 2 = below barcode
    
    // For CODE128, prefix with {B
    const barcodeContent = type === 73 ? `{B${content}` : content;
    sdk.printBarCode(type, barcodeContent, alignment);
  }

  /**
   * Print image/bitmap
   * @param {string} imageSource - Base64 data URL or image URL
   */
  printBitmap(imageSource) {
    const sdk = this.getSDK();
    if (sdk) sdk.printSingleBitmap(imageSource);
  }

  /**
   * Cut paper (if cutter available)
   */
  cutPaper() {
    const sdk = this.getSDK();
    if (sdk) sdk.partialCut();
  }

  /**
   * Open cash drawer (if available)
   */
  openCashDrawer() {
    const sdk = this.getSDK();
    if (sdk) sdk.openCashBox();
  }

  /**
   * Print a horizontal divider line
   */
  printDivider(char = '-') {
    const width = this.paperWidth === 58 ? 32 : 48;
    this.printText(char.repeat(width) + '\n');
  }

  /**
   * Print a receipt
   * @param {object} receipt - Receipt data
   */
  async printReceipt(receipt) {
    if (!this.isInitialized) {
      await this.initPrinter();
    }

    const {
      storeName = 'HostPoint POS',
      storeAddress = '',
      invoiceNumber = '',
      date = new Date().toLocaleString(),
      items = [],
      subtotal = 0,
      tax = 0,
      total = 0,
      paymentMethod = 'Cash',
      cashier = '',
      footer = 'Thank you for your business!'
    } = receipt;

    // Header
    this.setAlignment('center');
    this.setTextSize(32);
    this.setTextStyle('bold');
    this.printText(storeName + '\n');
    
    this.setTextSize(24);
    this.setTextStyle('normal');
    if (storeAddress) {
      this.printText(storeAddress + '\n');
    }
    
    this.printDivider('=');
    
    // Invoice info
    this.setAlignment('left');
    this.setTextSize(24);
    if (invoiceNumber) {
      this.printText(`Invoice: ${invoiceNumber}\n`);
    }
    this.printText(`Date: ${date}\n`);
    if (cashier) {
      this.printText(`Cashier: ${cashier}\n`);
    }
    
    this.printDivider();
    
    // Items
    for (const item of items) {
      const name = item.name || 'Item';
      const qty = item.quantity || 1;
      const price = item.price || 0;
      const itemTotal = item.total || (qty * price);
      
      this.printColumnsText(
        [`${qty}x ${name}`, itemTotal.toFixed(2)],
        [3, 1],
        [0, 2],
        [24, 24]
      );
    }
    
    this.printDivider();
    
    // Totals
    this.setAlignment('right');
    this.setTextSize(24);
    this.printText(`Subtotal: ${subtotal.toFixed(2)}\n`);
    if (tax > 0) {
      this.printText(`Tax: ${tax.toFixed(2)}\n`);
    }
    
    this.setTextSize(28);
    this.setTextStyle('bold');
    this.printText(`TOTAL: ${total.toFixed(2)}\n`);
    
    this.setTextStyle('normal');
    this.setTextSize(24);
    this.printText(`Payment: ${paymentMethod}\n`);
    
    this.printDivider('=');
    
    // Footer
    this.setAlignment('center');
    this.setTextSize(22);
    this.printText(footer + '\n');
    
    // Feed and cut
    this.feedPaper(100);
    this.cutPaper();
    
    return { success: true };
  }

  /**
   * Print a test page
   */
  async printTestPage() {
    if (!this.isInitialized) {
      await this.initPrinter();
    }

    const deviceInfo = this.deviceModel || { name: 'iMin Device' };
    const timestamp = new Date().toLocaleString();

    this.setPageFormat(this.paperWidth);
    
    this.printDivider('=');
    this.setAlignment('center');
    this.setTextSize(32);
    this.setTextStyle('bold');
    this.printText('HOSTPOINT POS\n');
    this.setTextSize(28);
    this.printText('TEST PRINT\n');
    this.printDivider('=');
    
    this.setAlignment('left');
    this.setTextSize(24);
    this.setTextStyle('normal');
    this.printText(`Device: ${deviceInfo.name}\n`);
    this.printText(`SDK: ${deviceInfo.sdkVersion || 'V1.0'}\n`);
    this.printText(`Connection: ${this.connectionType || 'USB'}\n`);
    this.printText(`Paper: ${this.paperWidth}mm\n`);
    this.printText(`Time: ${timestamp}\n`);
    
    this.printDivider();
    
    this.setAlignment('center');
    this.setTextSize(22);
    this.printText('Test Print Successful!\n');
    
    this.printDivider('=');
    
    this.feedPaper(100);
    this.cutPaper();

    return { success: true, device: deviceInfo.name, timestamp };
  }
}

// Singleton instance
const iminPrinter = new IminPrinterSDK();

export { IminPrinterSDK, iminPrinter };
export default iminPrinter;
